import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, X } from 'lucide-react';
import EmptyState from './EmptyState';

const SUPPORTED_FORMATS = ['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'PNG', 'JPG', 'JPEG', 'TXT'];

const UploadBox = ({ selectedFile, onFileSelect, onRemoveFile }) => {
  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (acceptedFiles.length > 0) {
      onFileSelect(acceptedFiles[0], null);
    } else if (fileRejections.length > 0) {
      const error = fileRejections[0].errors[0];
      let errorMsg = 'Invalid file.';
      if (error.code === 'file-too-large') {
        errorMsg = 'File is too large. Maximum size is 20MB.';
      } else if (error.code === 'file-invalid-type') {
        errorMsg = 'Unsupported file format.';
      }
      onFileSelect(null, errorMsg);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: 20 * 1024 * 1024, // 20 MB
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'text/plain': ['.txt'],
    },
    multiple: false,
  });

  return (
    <div className="w-full">
      <div
        {...getRootProps()}
        className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 cursor-pointer flex flex-col items-center justify-center min-h-[240px]
          ${isDragActive ? 'border-blue-500 bg-blue-50/50 scale-[1.02] shadow-inner' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'}`}
      >
        <input {...getInputProps()} />

        {selectedFile ? (
          <div className="flex flex-col items-center justify-center p-4 w-full">
             <div className="p-3 bg-green-50 rounded-full mb-3 shadow-sm">
               <FileText className="w-8 h-8 text-green-500" />
             </div>
             <p className="font-medium text-gray-800 text-center truncate max-w-xs">{selectedFile.name}</p>
             <p className="text-sm text-gray-500 mt-1">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</p>
             
             <button 
               type="button" 
               onClick={(e) => { e.stopPropagation(); onRemoveFile(); }}
               className="mt-4 flex items-center text-red-500 hover:text-red-700 text-sm font-medium transition-colors bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-md"
             >
               <X className="w-4 h-4 mr-1" />
               Remove File
             </button>
          </div>
        ) : (
          <EmptyState />
        )}
      </div>
      
      <div className="mt-5 flex flex-wrap justify-center gap-2 items-center">
        <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold mr-1">Supported formats:</span>
        {SUPPORTED_FORMATS.map(ext => (
          <span key={ext} className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-md font-medium border border-gray-200">
            {ext}
          </span>
        ))}
      </div>
    </div>
  );
};

export default UploadBox;
